import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpInterceptor,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, Subject, throwError, Subscriber } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { OAuthService } from 'angular-oauth2-oidc';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isTokenRefreshing = false;

  private tokenRefreshedSource: Subject<void> = new Subject<void>();
  private tokenRefreshed$: Observable<void> = this.tokenRefreshedSource.asObservable();

  private readonly redirectIgnoreUrls: string[] = [''];

  constructor(private readonly oauthService: OAuthService) {}

  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(this.updateRequestHeader(request)).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.refreshToken(request).pipe(
            switchMap(() => next.handle(this.updateRequestHeader(request))),
            catchError((error) => {
              this.oauthService.logOut();

              return throwError(error);
            }),
          );
        }

        return throwError(error);
      }),
    );
  }

  private updateRequestHeader(request: HttpRequest<unknown>): HttpRequest<unknown> {
    const token = this.oauthService.getAccessToken();
    if (token) {
      const headers = request.headers.set('Authorization', this.oauthService.authorizationHeader());
      request = request.clone({ headers });
    }

    return request;
  }

  private refreshToken(request: HttpRequest<unknown>): Observable<HttpEvent<unknown>> {
    if (this.isTokenRefreshing) {
      return this.getNewRefreshObservable();
    } else {
      this.isTokenRefreshing = true;

      return new Observable((observer) => {
        this.oauthService
          .refreshToken()
          .then(
            () => {
              this.tokenRefreshedSource.next();
              observer.next({} as HttpEvent<unknown>);
              observer.complete();
            },
            () => this.onRefreshError(request, observer),
          )
          .catch(() => this.onRefreshError(request, observer))
          .finally(() => (this.isTokenRefreshing = false));
      });
    }
  }

  private onRefreshError(
    request: HttpRequest<unknown>,
    observer: Subscriber<HttpEvent<unknown>>,
  ): void {
    observer.error('Cannot refresh token');
    if (!this.redirectIgnoreUrls.some((url) => request.url.includes(url))) {
      this.oauthService.loadDiscoveryDocumentAndLogin().then();
    }
  }

  private getNewRefreshObservable(): Observable<HttpEvent<unknown>> {
    return new Observable((observer) => {
      this.tokenRefreshed$.subscribe(() => {
        observer.next({} as HttpEvent<unknown>);
        observer.complete();
      });
    });
  }
}
